import * as React from 'react';
import Avatar from 'antd/lib/avatar';
import Divider from 'antd/lib/divider';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import { AICodeAuthorProvider, CommitItem, PRItem } from '@/types';
import { isCommit } from './featureReadinessHelpers';
import { antGrey, getAvatarColorForName } from '../styles/globalStyles';
import claudeCodeAvatar from '../../../assets/ai-avatar-claude-code.svg';
import cursorAvatar from '../../../assets/ai-avatar-cursor.svg';
import githubCopilotAvatar from '../../../assets/ai-avatar-github-copilot.svg';

const providerDisplayNames: Record<AICodeAuthorProvider, string> = {
    [AICodeAuthorProvider.Cursor]: 'Cursor',
    [AICodeAuthorProvider.Claude]: 'Claude',
    [AICodeAuthorProvider.Copilot]: 'Copilot',
};

const AI_CO_AUTHOR_PROVIDER_DISPLAY_ORDER = [
    AICodeAuthorProvider.Claude,
    AICodeAuthorProvider.Copilot,
    AICodeAuthorProvider.Cursor,
];

const providerAvatarSrc: Record<AICodeAuthorProvider, string> = {
    [AICodeAuthorProvider.Claude]: claudeCodeAvatar,
    [AICodeAuthorProvider.Cursor]: cursorAvatar,
    [AICodeAuthorProvider.Copilot]: githubCopilotAvatar,
};

export const displayNameForAICodeAuthorProvider = (provider: AICodeAuthorProvider): string =>
    providerDisplayNames[provider] ?? provider;

export const getPrimaryAICoAuthorDisplayName = (aiCoAuthorProviders?: AICodeAuthorProvider[]): string | null => {
    if (aiCoAuthorProviders?.length) {
        return displayNameForAICodeAuthorProvider(aiCoAuthorProviders[0]);
    }
    return null;
};

export const getPrimaryAICodeAuthorProvider = (
    aiCoAuthorProviders?: AICodeAuthorProvider[],
): AICodeAuthorProvider | null => aiCoAuthorProviders?.[0] ?? null;

export const getPercentageAICoAuthoredForCodeItem = (code: CommitItem | PRItem): number | undefined => {
    if (isCommit(code)) {
        return code.sourcePR?.percentageAICoAuthored;
    }

    return code.percentageAICoAuthored;
};

const sortAICoAuthorProviders = (providers: AICodeAuthorProvider[]): AICodeAuthorProvider[] => {
    const uniqueProviders = Array.from(new Set(providers));

    return uniqueProviders.sort(
        (left, right) =>
            AI_CO_AUTHOR_PROVIDER_DISPLAY_ORDER.indexOf(left) - AI_CO_AUTHOR_PROVIDER_DISPLAY_ORDER.indexOf(right),
    );
};

export const formatAICoAuthorProvidersList = (aiCoAuthorProviders: AICodeAuthorProvider[]): string =>
    sortAICoAuthorProviders(aiCoAuthorProviders)
        .map((provider) => displayNameForAICodeAuthorProvider(provider))
        .join(', ');

export const formatCodeOwnerWithAICoAuthor = (
    codeOwner: string,
    aiCoAuthorProviders?: AICodeAuthorProvider[],
): string => {
    if (!aiCoAuthorProviders?.length) {
        return codeOwner;
    }

    const providersLabel = formatAICoAuthorProvidersList(aiCoAuthorProviders);
    if (!codeOwner) {
        return providersLabel;
    }

    return `${codeOwner} and ${providersLabel}`;
};

export const formatCodeOwnerRowTooltip = (
    codeOwner: string,
    aiCoAuthorProviders: AICodeAuthorProvider[],
    percentageAICoAuthored?: number,
): string => {
    const providersLabel = formatAICoAuthorProvidersList(aiCoAuthorProviders);
    let content: string;
    if (!codeOwner) {
        content = providersLabel;
    } else if (!providersLabel) {
        content = codeOwner;
    } else {
        content = `${codeOwner}, ${providersLabel}`;
    }

    return appendPercentageAICoAuthoredToTooltip(content, percentageAICoAuthored);
};

export const formatAICoAuthorsList = (aiCoAuthors: string[]): string => aiCoAuthors.join(', ');

const getDisplayPercentageAICoAuthored = (percentageAICoAuthored: number): number =>
    percentageAICoAuthored <= 1 ? Math.round(percentageAICoAuthored * 100) : Math.round(percentageAICoAuthored);

export const formatPercentageAICoAuthored = (percentageAICoAuthored: number): string => {
    const pct = getDisplayPercentageAICoAuthored(percentageAICoAuthored);
    return `${pct}% AI by commit volume`;
};

export const appendPercentageAICoAuthoredToTooltip = (content: string, percentageAICoAuthored?: number): string => {
    if (!content || percentageAICoAuthored == null || Number.isNaN(percentageAICoAuthored)) {
        return content;
    }

    if (getDisplayPercentageAICoAuthored(percentageAICoAuthored) === 0) {
        return content;
    }

    return `${content} (${formatPercentageAICoAuthored(percentageAICoAuthored)})`;
};

interface AICoAuthorDetailRowProps {
    aiCoAuthors: string[];
    percentageAICoAuthored?: number;
}

export const AICoAuthorDetailRow: React.FC<AICoAuthorDetailRowProps> = ({ aiCoAuthors, percentageAICoAuthored }) => {
    if (!aiCoAuthors.length) {
        return null;
    }

    const authorsLabel = formatAICoAuthorsList(aiCoAuthors);
    const tooltipContent =
        (
            percentageAICoAuthored != null &&
            !Number.isNaN(percentageAICoAuthored) &&
            getDisplayPercentageAICoAuthored(percentageAICoAuthored) !== 0
        ) ?
            formatPercentageAICoAuthored(percentageAICoAuthored)
        :   null;

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 16,
                    color: antGrey.grey8,
                }}
            >
                <Text style={{ color: antGrey.grey8, whiteSpace: 'nowrap', flexShrink: 0 }}>AI Co-author(s)</Text>
                <Tooltip title={tooltipContent} placement="left">
                    <Text
                        style={{
                            color: antGrey.grey8,
                            cursor: 'default',
                            flex: 1,
                            minWidth: 0,
                            textAlign: 'right',
                            overflowWrap: 'anywhere',
                        }}
                    >
                        {authorsLabel}
                    </Text>
                </Tooltip>
            </div>
            <Divider style={{ margin: '16px 0px' }} />
        </>
    );
};

const CODE_OWNER_AVATAR_SIZE = 20;
const CODE_OWNER_AVATAR_OFFSET = 12;

const codeOwnerAvatarStyle = (codeOwner: string, baseStyle: React.CSSProperties = {}): React.CSSProperties => {
    const backgroundColor = getAvatarColorForName(codeOwner);

    return {
        ...baseStyle,
        backgroundColor,
        background: backgroundColor,
        opacity: 1,
    };
};

interface AICodeAuthorProviderIconProps {
    provider: AICodeAuthorProvider;
    size?: number;
}

export const AICodeAuthorProviderIcon: React.FC<AICodeAuthorProviderIconProps> = ({
    provider,
    size = CODE_OWNER_AVATAR_SIZE,
}) => (
    <img
        src={providerAvatarSrc[provider]}
        width={size}
        height={size}
        alt=""
        style={{
            borderRadius: '50%',
            flexShrink: 0,
            display: 'block',
        }}
    />
);

interface CodeOwnerAvatarProps {
    codeOwner: string;
    aiProvider?: AICodeAuthorProvider | null;
}

export const CodeOwnerAvatar: React.FC<CodeOwnerAvatarProps> = ({ codeOwner, aiProvider }) => {
    const baseAvatarStyle: React.CSSProperties = {
        fontSize: 10,
    };

    if (aiProvider && !codeOwner) {
        return <AICodeAuthorProviderIcon provider={aiProvider} size={CODE_OWNER_AVATAR_SIZE} />;
    }

    if (!aiProvider) {
        return (
            <Avatar
                style={{
                    ...codeOwnerAvatarStyle(codeOwner, baseAvatarStyle),
                    flexShrink: 0,
                }}
                size={CODE_OWNER_AVATAR_SIZE}
            >
                {codeOwner.charAt(0).toUpperCase()}
            </Avatar>
        );
    }

    return (
        <div
            style={{
                position: 'relative',
                width: CODE_OWNER_AVATAR_SIZE + CODE_OWNER_AVATAR_OFFSET,
                height: CODE_OWNER_AVATAR_SIZE + CODE_OWNER_AVATAR_OFFSET,
                flexShrink: 0,
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    left: CODE_OWNER_AVATAR_OFFSET,
                    bottom: 0,
                    zIndex: 0,
                }}
            >
                <AICodeAuthorProviderIcon provider={aiProvider} size={CODE_OWNER_AVATAR_SIZE} />
            </div>
            <Avatar
                style={{
                    ...codeOwnerAvatarStyle(codeOwner, baseAvatarStyle),
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    zIndex: 1,
                }}
                size={CODE_OWNER_AVATAR_SIZE}
            >
                {codeOwner.charAt(0).toUpperCase()}
            </Avatar>
        </div>
    );
};
